import React, { useMemo, useState } from 'react';
import NoProductImage from "./components/NoProductImage";
import MarkdownViewer from './components/MarkdownViewer';
        
export type ProductPreviewCardProps = {
  id?: string | number;
  name?: string;
  brand?: string;
  category?: string;
  strain?: string;
  inventoryUpc?: string;
  imageName?: string;
  imageUrl?: string;
  imageUrls?: string[];
  markets?: string[];
  description?: string;
  isArchived?: boolean;
  archivedAt?: string | null;
  showStatusBadge?: boolean;
  archiveHelp?: string;
  emptyNameLabel?: string;
  emptyBrandLabel?: string;
  emptyMetaLabel?: string;
  showEmptyPlaceholders?: boolean;
  previewEnabled?: boolean;
  mode?: 'default' | 'add';
  container?: 'card' | 'plain';
  testId?: string;
};

export default function ProductPreviewCard({
  id,
  name,
  brand,
  category,
  strain,
  inventoryUpc,
  imageName: _imageName,
  imageUrl,
  imageUrls,
  markets,
  description,
  isArchived,
  archivedAt,
  showStatusBadge = false,
  archiveHelp,
  emptyNameLabel = '—',
  emptyBrandLabel = '—',
  emptyMetaLabel = '—',
  showEmptyPlaceholders = true,
  previewEnabled = true,
  mode: _mode = 'default',
  container = 'card',
  testId,
}: ProductPreviewCardProps) {
  const resolvedTestId = testId || `product-preview-${id ?? 'new'}`;
  const emptyText = showEmptyPlaceholders ? '—' : '';

  const displayName = String(name || '').trim()
    ? name
    : (showEmptyPlaceholders ? emptyNameLabel : '');
  const displayBrand = String(brand || '').trim()
    ? brand
    : (showEmptyPlaceholders ? emptyBrandLabel : '');
  const displayCategory = category?.trim() || '';
  const displayStrain = String(strain || '').trim() ? strain : '';
  const displayUpc = String(inventoryUpc || '').trim() ? inventoryUpc : '';

  const displayStatus = isArchived ? 'Archived' : 'Active';
  const displayArchivedAt = String(archivedAt || '').trim() ? archivedAt : '';
  const displayDescription = String(description || '').trim() ? description : '';

  const selectedMarkets = Array.isArray(markets) ? markets : [];

  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const images = useMemo(() => {
    const provided = Array.isArray(imageUrls)
      ? imageUrls.map((u) => String(u || '').trim()).filter(Boolean)
      : [];
    const single = String(imageUrl || '').trim();

    if (provided.length) return provided;
    if (single) return [single];
    return [];
  }, [imageUrl, imageUrls]);

  const safeActiveIndex = Math.min(Math.max(activeImageIndex, 0), Math.max(images.length - 1, 0));
  const computedImageUrl = images[safeActiveIndex] || '';

  const marketText = selectedMarkets.length
    ? `${selectedMarkets.length} market${selectedMarkets.length === 1 ? '' : 's'}`
    : '';
  const metaParts = [displayCategory, displayStrain, marketText].filter(Boolean);
  const metaLine = metaParts.length
    ? metaParts.join(' • ')
    : (showEmptyPlaceholders ? emptyMetaLabel : '');

  const outerClassName = container === 'card' ? 'card live-preview-shell' : 'live-preview-shell';

  return (
    <div className={outerClassName} data-testid={resolvedTestId}>
      {previewEnabled ? (
        <div className="live-preview-title">
          <span>Live Product Preview</span> 
          {showStatusBadge && <span className="live-preview-title-badge badge" data-testid={`${resolvedTestId}-status`}>{displayStatus}</span>}
        </div>
      ) : null}

      <div className="live-preview-card">
        <div className="live-preview-card-top">
          {displayBrand ? <div className="live-preview-brand" data-testid={`${resolvedTestId}-brand`}>{displayBrand}</div> : null}

          <div className="live-preview-name-row">
            <div className="live-preview-name" data-testid={`${resolvedTestId}-name`}>{displayName}</div>
          </div>

          {metaLine ? <div className="live-preview-meta" data-testid={`${resolvedTestId}-meta`}>{metaLine}</div> : null}
        </div>

        <div className="live-preview-media" aria-label="Product media preview" data-testid={`${resolvedTestId}-media`}>
          <div className={`live-preview-media-hero${computedImageUrl ? ' live-preview-media-hero--filled' : ' live-preview-media-hero--empty'}`}>
            {computedImageUrl ? (
              <img src={computedImageUrl} alt={String(displayName || 'Product image')} data-testid={`${resolvedTestId}-image`} />
            ) : (
              <div className="live-preview-media-empty" aria-label="No product image" data-testid={`${resolvedTestId}-image-empty`}>
                <NoProductImage />
              </div>
            )}
          </div>

          {images.length > 1 ? (
            <div className="live-preview-carousel-dots" role="tablist" aria-label="Product images" data-testid={`${resolvedTestId}-image-tabs`}>
              {images.map((url, idx) => (
                <button
                  key={`${url}-${idx}`}
                  type="button"
                  className={`live-preview-carousel-dot${idx === safeActiveIndex ? ' is-active' : ''}`}
                  onClick={() => setActiveImageIndex(idx)}
                  role="tab"
                  aria-selected={idx === safeActiveIndex}
                  aria-label={`Select image ${idx + 1} of ${images.length}`}
                  data-testid={`${resolvedTestId}-image-tab-${idx}`}
                />
              ))}
            </div>
          ) : null}
        </div>
            {(displayDescription || showEmptyPlaceholders) ? (
              <div className="live-preview-description" data-color-mode="light" data-testid={`${resolvedTestId}-description`}>
                {displayDescription ? (
                  <MarkdownViewer
                  markdown={displayDescription}
                  />
                ) : (
                  emptyText
                )}
              </div>
            ) : null}

            {(displayUpc || showEmptyPlaceholders) ? (
              <div className="live-preview-inline-row" data-testid={`${resolvedTestId}-inventory-upc`}>
                <span className="live-preview-inline-label">Inventory UPC:</span>
                <span className="live-preview-inline-value">{displayUpc || emptyText}</span>
              </div>
            ) : null}

        {String(archiveHelp || '').trim() ? (
          <div className="live-preview-help muted" data-testid={`${resolvedTestId}-archive-help`}>{archiveHelp}</div>
        ) : null}

        {isArchived && displayArchivedAt ? (
          <div className="live-preview-help muted" data-testid={`${resolvedTestId}-archived-at`}>Archived At: {displayArchivedAt}</div>
        ) : null}
      </div>
    </div>
  );
}
